import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action,
} from 'vuex-module-decorators';
import store from '@/store';
import Program from '@/models/Program';
import { getProgramsGPSOnlyPrescriber } from '@/api/api';
import MarkerGPS from '@/models/MarkerGPS';
import MetaDatatable from '@/models/MetaDatatable';
import transformFilters from '@/utils/transformFilters';
import FiltersModule from './FiltersModule';
import taxSystems from '@/data/taxSystems';

const GEOJSON_DEPARTEMENTS = 'https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/departements.geojson';
const GEOJSON_REGIONS = 'https://raw.githubusercontent.com/gregoiredavid/france-geojson/master/regions.geojson';

let _cachedDepartements: any = null;
let _cachedRegions: any = null;
let mapFetchSeq = 0;

interface Value {
  latitude: number;
  longitude: number;
  zoom: number;
}

interface LocationCenter {
  latitude: number;
  longitude: number;
  zoom: number;
  type: 'city' | 'department' | 'region' | 'country';
}

interface MapState {
  shownValue: Value | null;
  markers: MarkerGPS[];
  locationCenter: LocationCenter | null;
  locationBoundaries: any | null;
}

@Module({ dynamic: true, store, name: 'map', namespaced: true })
class MapModule extends VuexModule implements MapState {
  public shownValue: Value | null = null;
  public markers: MarkerGPS[] = [];
  public loading: boolean = false;
  public locationCenter: LocationCenter | null = null;
  public locationBoundaries: any | null = null;

  @Mutation
  setShownValue(value: Value) {
    this.shownValue = value;
  }

  @Mutation
  setMarkers(markers: MarkerGPS[]) {
    this.markers = markers;
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  // Nouvelle mutation pour le centrage sur localisation
  @Mutation
  setLocationCenter(locationCenter: LocationCenter | null) {
    this.locationCenter = locationCenter;
  }

  @Action
  async showOnMap(program: Program) {
    if (
      program.address &&
      program.address.latitude &&
      program.address.longitude
    ) {
      const value: Value = {
        latitude: program.address.latitude,
        longitude: program.address.longitude,
        zoom: 16,
      };
      this.context.commit('setShownValue', value);
    }
  }

  // Nouvelle action pour centrer sur une ville
  @Action
  async centerOnCity(cityName: string) {
    console.log('🎯 MapModule.centerOnCity appelé avec:', cityName);
    try {
      // Appeler la méthode de géocodage directement ici
      const coordinates = await this.getCityCoordinatesAction(cityName);
      console.log('📍 Coordonnées récupérées:', coordinates);
      
      if (coordinates) {
        const locationCenter: LocationCenter = {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          zoom: 12, // Zoom approprié pour une ville
          type: 'city'
        };
        console.log('🗺️ Setting locationCenter:', locationCenter);
        this.context.commit('setLocationCenter', locationCenter);
      } else {
        console.log('❌ Aucune coordonnée trouvée pour:', cityName);
      }
    } catch (error) {
      console.error('❌ Erreur lors du centrage sur la ville:', error);
    }
  }

  // Nouvelle action pour centrer sur un département
  @Action
  async centerOnDepartment(departmentCode: string) {
    try {
      const coordinates = await this.getDepartmentCoordinatesAction(departmentCode);
      if (coordinates) {
        const locationCenter: LocationCenter = {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          zoom: 9, // Zoom approprié pour un département
          type: 'department'
        };
        this.context.commit('setLocationCenter', locationCenter);
      }
    } catch (error) {
      console.error('Erreur lors du centrage sur le département:', error);
    }
  }

  // Nouvelle action pour centrer sur une région
  @Action
  async centerOnRegion(regionCode: string) {
    try {
      const coordinates = await this.getRegionCoordinatesAction(regionCode);
      if (coordinates) {
        const locationCenter: LocationCenter = {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          zoom: 7, // Zoom approprié pour une région
          type: 'region'
        };
        this.context.commit('setLocationCenter', locationCenter);
      }
    } catch (error) {
      console.error('Erreur lors du centrage sur la région:', error);
    }
  }


  @Action
async centerOnCountry(countryCode: string) {
  let countryCenter: { lat: number; lng: number; zoom: number } | null = null;

  if (countryCode === 'FR') {
    // France : centre géographique
    countryCenter = { lat: 46.603354, lng: 1.888334, zoom: 6 };
  } else if (countryCode === 'ES') {
    // Espagne : centre géographique
    countryCenter = { lat: 40.463667, lng: -3.74922, zoom: 6 };
  } else if (countryCode === 'AE') {
    // EAU : centre géographique
    countryCenter = { lat: 23.424076, lng: 53.847818, zoom: 6 };
  }

  if (countryCenter) {
    const locationCenter: LocationCenter = {
      latitude: countryCenter.lat,
      longitude: countryCenter.lng,
      zoom: countryCenter.zoom,
      type: 'country'
    };
    this.context.commit('setLocationCenter', locationCenter);
  }
}




  @Mutation
  setLocationBoundaries(boundaries: any) {
    this.locationBoundaries = boundaries;
  }

  @Action
  resetLocationCenter() {
    this.context.commit('setLocationCenter', null);
  }

  @Action
  clearLocationBoundaries() {
    this.context.commit('setLocationBoundaries', null);
  }

  @Action
  async fetchLocationBoundaries() {
    const filters = FiltersModule.filters as any;
    const deptCodes: string[] = filters.departement
      ? (Array.isArray(filters.departement) ? filters.departement : [filters.departement])
      : [];
    const regionCodes: string[] = filters.region || [];
    const cityNames: string[] = filters.city || [];

    if (!deptCodes.length && !regionCodes.length && !cityNames.length) {
      this.context.commit('setLocationBoundaries', null);
      return;
    }

    const features: any[] = [];

    try {
      if (deptCodes.length) {
        if (!_cachedDepartements) {
          const r = await fetch(GEOJSON_DEPARTEMENTS);
          _cachedDepartements = await r.json();
        }
        _cachedDepartements.features.forEach((f: any) => {
          if (deptCodes.includes(f.properties.code)) {
            features.push({ ...f, properties: { ...f.properties, locationType: 'department' } });
          }
        });
      }

      if (regionCodes.length) {
        if (!_cachedRegions) {
          const r = await fetch(GEOJSON_REGIONS);
          _cachedRegions = await r.json();
        }
        _cachedRegions.features.forEach((f: any) => {
          if (regionCodes.includes(f.properties.code)) {
            features.push({ ...f, properties: { ...f.properties, locationType: 'region' } });
          }
        });
      }

      for (const cityName of cityNames) {
        try {
          const r = await fetch(
            `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(cityName)}&format=geojson&geometry=contour&limit=1`
          );
          const data = await r.json();
          if (data.features && data.features.length > 0) {
            features.push({ ...data.features[0], properties: { ...data.features[0].properties, locationType: 'city' } });
          }
        } catch { /* skip individual city failures */ }
      }
    } catch (error) {
      console.error('Error fetching location boundaries:', error);
    }

    this.context.commit('setLocationBoundaries', features.length > 0 ? { type: 'FeatureCollection', features } : null);
  }

  // Actions pour récupérer les coordonnées (au lieu de méthodes privées)
    @Action
    async getCityCoordinatesAction(cityName: string): Promise<{lat: number, lng: number} | null> {
      console.log('🌐 Recherche coordonnées pour:', cityName);
      try {
        // CHANGED HERE:
        const url = `https://data.geopf.fr/geocodage/search?q=${encodeURIComponent(cityName)}&type=municipality&limit=1`;
        console.log('📡 URL API:', url);
        
        const response = await fetch(url);
        const data = await response.json();
        console.log('📥 Réponse API:', data);
        
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].geometry.coordinates;
          console.log('✅ Coordonnées trouvées:', { lat, lng });
          return { lat, lng };
        }
        console.log('❌ Aucune coordonnée dans la réponse');
        return null;
      } catch (error) {
        console.error('❌ Erreur géocodage ville:', error);
        return null;
      }
    }


  @Action
  async getDepartmentCoordinatesAction(departmentCode: string): Promise<{lat: number, lng: number} | null> {
    try {
      // API pour récupérer les coordonnées d'un département
      const response = await fetch(`https://geo.api.gouv.fr/departements/${departmentCode}?fields=centre`);
      const data = await response.json();
      
      if (data.centre) {
        return { lat: data.centre.coordinates[1], lng: data.centre.coordinates[0] };
      }
      return null;
    } catch (error) {
      console.error('Erreur géocodage département:', error);
      return null;
    }
  }

  @Action
  async getRegionCoordinatesAction(regionCode: string): Promise<{lat: number, lng: number} | null> {
    try {
      // API pour récupérer les coordonnées d'une région
      const response = await fetch(`https://geo.api.gouv.fr/regions/${regionCode}?fields=centre`);
      const data = await response.json();
      
      if (data.centre) {
        return { lat: data.centre.coordinates[1], lng: data.centre.coordinates[0] };
      }
      return null;
    } catch (error) {
      console.error('Erreur géocodage région:', error);
      return null;
    }
  }



  @Action
  async fetch() {
      // Latest request wins: an older, slower response must not overwrite newer markers
      const seq = ++mapFetchSeq;
      this.context.commit('setLoading', true);
      const filters = transformFilters(FiltersModule.filters);
      const meta: MetaDatatable = {
          total: -1,
          pages: -1,
          page: -1,
          perPage: -1,
          search: null,
          orderBy: null,
          orderWay: null,
          filters,
      };
      const { data } = await getProgramsGPSOnlyPrescriber(meta);
      if (seq !== mapFetchSeq) return;
      const programs = data.data;
      const widthCoordinates = programs.filter(
          (el: Program) => el.address && el.address.latitude && el.address.longitude
      );
      const markers: MarkerGPS[] = widthCoordinates.map((el: Program) => {
          let color = '#ffffff';
          if (el.tax_system && el.tax_system.length > 0) {
              // @ts-ignore
              const tax_sys = taxSystems.find(sys => sys.value === el.tax_system[0]);
              if (tax_sys) {
                  color = tax_sys.color;
              }
          }
          return {
              programID: el.id || 0,
              color,
              coordinates: {
                  lng: el.address ? el.address.longitude || 0 : 0,
                  lat: el.address ? el.address.latitude || 0 : 0,
              },
          }
      });
      this.context.commit('setMarkers', markers);
      this.context.commit('setLoading', false);
  }


}

export default getModule(MapModule);